import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import * as Sentry from '@sentry/angular';

import { AppComponent } from './app.component';
import { FavouritesModule } from './favourites/favourites.module';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared';
import { CoreModule } from './core/core.module';
import { AuthEffects } from './store/auth/auth.effects';

import { LabsysContactEffects } from './store/labsys-contact/labsys-contact.effects';
import { LayoutEffects, LayoutSamplingSuppliesEffects } from './store/layout/layout.effects';
import { environment } from '../environments/environment';
import { CustomSerializer, metaReducers, reducers } from './store/app.reducers';
import { CartEffects } from './store/cart/cart.effects';
import { SamplingSuppliesEffects } from './store/sampling-supplies/sampling-supplies.effects';
import { SharedCartFavouritesLinkModule } from './shared-cart-favourites-link/shared-cart-favourites-link.module';
import { SettingsModule } from './settings/settings.module';

import { EffectsModule } from '@ngrx/effects';
import { Router } from '@angular/router';
import { RouterState, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { ShellModule } from './shell/shell.module';
import { HomeModule } from '@app/home/home.module';
import { FormsModule } from '@angular/forms';
import * as fromOrderCheckout from '@app/order-checkout/store';
import { OrderCheckoutEffects } from '@app/order-checkout/store/order-checkout/order-checkout.effects';
import { UserDetailsEffects } from '@app/order-checkout/store/user-details/user-details.effects';
import { OrderReferencesEffects } from '@app/order-checkout/store/order-references/order-references.effects';
import { DeliveryEffects } from '@app/order-checkout/store/delivery-details/delivery-details.effects';
import { BillingDetailsEffects } from '@app/order-checkout/store/billing-details/billing-details.effects';
import { OnlineTermsComponent } from './pages/online-terms/online-terms.component';
import { UserOnBoardingModule } from '@app/user-on-boarding/user-on-boarding.module';
import { PaymentsEffects } from './store/payments/payments.effects';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { BASE_URL, EventsClient, AuthClient, ProfileClient } from '@app/clients/client.generated';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

@NgModule({
    imports: [
        BrowserModule,
        FormsModule,
        BrowserAnimationsModule,
        // core & shared
        CoreModule,
        SharedModule,
        // features
        FavouritesModule,
        SharedCartFavouritesLinkModule,
        SettingsModule,

        StoreModule.forRoot(reducers, {
            metaReducers: metaReducers,
            runtimeChecks: { strictStateImmutability: false, strictActionImmutability: false } }),

        StoreRouterConnectingModule.forRoot({
            routerState: RouterState.Full,
        }),
        !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
        EffectsModule.forRoot([
            CartEffects, SamplingSuppliesEffects,
            LabsysContactEffects, LayoutSamplingSuppliesEffects, AuthEffects, LayoutEffects]),
        // load early those store modules needed in direct links : on behalf
        StoreModule.forFeature('orderCheckout', fromOrderCheckout.reducers, { metaReducers: fromOrderCheckout.metaReducers }),
        EffectsModule.forFeature([
            OrderCheckoutEffects,
            UserDetailsEffects,
            OrderReferencesEffects,
            DeliveryEffects,
            BillingDetailsEffects,
            PaymentsEffects]),
        UserOnBoardingModule, // init on load to stop guard navigations from being cancelled
        ShellModule,
        HomeModule,
        AppRoutingModule,
        RecaptchaV3Module
    ],
    declarations: [
        AppComponent,
        OnlineTermsComponent
    ],
    providers: [
        // Without registering this, Angular errors won't be captured (you'll only see console errors locally).
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
        { provide: RouterStateSerializer, useClass: CustomSerializer },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: environment.recaptchaKey
        },
        provideAnimationsAsync(),
        { provide: BASE_URL, useValue: environment.myLabBaseUrl },
        EventsClient,
        AuthClient,
        ProfileClient,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
